import * as React from 'react';
import { isDefined, noop } from '@react-financial-charts/core';
import type { MoreProps } from '@/types/commontypes';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { ClickableCircle } from './index';
import type { EachTrendLineProps } from './EachTrendLine';
import { findYpoint, InteractiveStraightLineWithoutPoint } from './InteractiveStraightLineWithoutPoint';

interface EachVertLineState {
  anchor?: string;
  dragging?: boolean;
}

export class EachVertLine extends React.Component<EachTrendLineProps, EachVertLineState> {
  public static defaultProps = {
    onDrag: noop,
    onEdge1Drag: noop,
    onEdge2Drag: noop,
    selected: false,
    edgeStroke: '#1e53e6',
    edgeFill: '#FFFFFF',
    edgeStrokeWidth: 2,
    r: 5,
    strokeWidth: 1,
    strokeDasharray: 'Solid',
    extendLeft: false,
    extendRight: false,
    arrowDirection: 'none',
    hoverText: {
      enable: false,
    },
    locked: false,
  };

  private dragStart!: { x1Value: number; y1Value: number; x2Value: number; y2Value: number };

  private saveNodeType: <T>(type: string) => React.Ref<T>;
  prevXDomain = null;
  // @ts-expect-error
  private lineInteractive: string;

  public constructor(props: EachTrendLineProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);

    this.state = {};
  }

  public render() {
    const {
      x1Value,
      x2Value,
      type,
      strokeStyle,
      strokeWidth,
      strokeDasharray,
      r,
      edgeStrokeWidth,
      edgeFill,
      edgeStroke,
      hoverText,
      selected,
      locked,
      onDoubleClick,
      overrideData,
      uuid,
    } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { dragging } = this.state;
    // let lineInteractive;
    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }

    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;

    const hoverState = selected || overrideData?.hover === uuid;
    return (
      <g>
        <InteractiveStraightLineWithoutPoint
          ref={this.saveNodeType('line')}
          selected={hoverState === true}
          onHover={this.handleHover}
          onUnHover={this.handleUnhover}
          x1Value={x1Value}
          y1Value={undefined}
          x2Value={x2Value}
          y2Value={undefined}
          type={type}
          strokeStyle={strokeStyle}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          interactiveCursorClass={this.lineInteractive}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineDrag}
          onDragComplete={this.handleDragComplete}
          extendLeft={false}
          extendRight={false}
          onMouseDown={this.handleShapeMouseDown}
          onDoubleClick={onDoubleClick}
        />

        <ClickableCircle
          ref={this.saveNodeType('edgeCenter')}
          show={hoverState}
          cx={x2Value}
          r={lockedR}
          fillStyle={edgeFill}
          strokeStyle={lockedEdgeStorke}
          strokeWidth={lockedStrokeWidth}
          onDoubleClick={onDoubleClick}
        />

        <HoverTextNearMouse
          show={hoverTextEnabled && overrideData?.hover === uuid}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    this.updateCircleX2position(e, moreProps);
    if (onSelect && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, overrideData, onOverride, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps.hovering;
    if (overrideData?.hover !== uuid) {
      onOverride?.({
        hover: uuid,
      });

      if (hovering && onHover) {
        onHover(uuid ?? null, moreProps);
      }
    }
  };

  private readonly handleUnhover = (_: React.MouseEvent, _moreProps: MoreProps) => {
    const { overrideData, onOverride, uuid } = this.props;
    if (overrideData?.hover === uuid) {
      onOverride?.({
        hover: undefined,
      });
    }
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onDragComplete, locked } = this.props;
    if (onDragComplete === undefined || locked) {
      return;
    }
    this.setState({
      anchor: undefined,
      dragging: false,
    });
    onDragComplete(e, moreProps);
  };

  private readonly handleLineDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, locked } = this.props;

    if (locked) return;
    const { x1Value, y1Value, x2Value, y2Value } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
      hovering,
      startPos: startPosRaw,
      mouseXY,
    } = moreProps;
    const startPos = startPosRaw!;

    if (!hovering) return;

    // Validate initial values before calculation
    if (x1Value == null || y1Value == null || x2Value == null || y2Value == null) {
      return;
    }

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    // Validate scale results
    if (!isFinite(x1) || !isFinite(y1) || !isFinite(x2) || !isFinite(y2)) {
      return;
    }

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullData);
    const newX2Value = getXValue(xScale, xAccessor, [x2 - dx, y2 - dy], fullData);

    // Validate calculated values before passing to onDrag
    if (newX1Value == null || newX2Value == null) {
      return;
    }

    this.setState({ dragging: true });

    onDrag(
      e,
      uuid,
      {
        x1Value: newX1Value,
        y1Value,
        x2Value: newX2Value,
        y2Value,
      },
      moreProps,
    );
  };

  private readonly handleLineDragStart = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { x1Value, y1Value, x2Value, y2Value, onDragStart, uuid, direction: _direction, locked } = this.props;
    this.updateCircleX2position(e, moreProps);
    if (locked) return;

    // Validate values before starting drag
    if (x1Value == null || y1Value == null || x2Value == null || y2Value == null) {
      return;
    }

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    this.setState({ dragging: true });
    this.dragStart = {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
    };
  };

  private readonly updateCircleX2position = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x2Value, y1Value, x1Value } = this.props;

    const { y1: _y1, y2 } = findYpoint(moreProps);
    onDrag(
      e,
      uuid,
      {
        x1Value,
        y1Value,
        x2Value,
        y2Value: y2,
      },
      moreProps,
    );
  };
}
